// src/pages/ContentDrafts.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const PLATFORMS = [
  { value: "twitter", label: "Twitter" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "discord", label: "Discord" },
  { value: "telegram", label: "Telegram" },
  { value: "gmail", label: "Email" },
];
const SOCIAL_TYPES = ["post", "reply", "comment"];

function Banner({ notice }) {
  if (!notice) return null;
  const ok = notice.type === "success";
  const style = {
    padding: "10px 12px",
    marginBottom: 12,
    borderRadius: 8,
    fontSize: 14,
    background: ok ? "#e8f5e9" : "#ffebee",
    border: `1px solid ${ok ? "#c8e6c9" : "#ffcdd2"}`,
    color: ok ? "#1b5e20" : "#b71c1c",
  };
  return <div style={style}>{notice.msg}</div>;
}

export default function ContentDrafts() {
  const [rows, setRows] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [editText, setEditText] = useState({});
  const [editSubject, setEditSubject] = useState({});
  const [editImageUrl, setEditImageUrl] = useState({});
  const [reviewerEmail, setReviewerEmail] = useState(null);
  const [rewriteOpenId, setRewriteOpenId] = useState(null);
  const [rewriteFeedback, setRewriteFeedback] = useState({});
  // Reject panel: "No + explain why" with an optional note for Claire to learn from.
  const [rejectOpenId, setRejectOpenId] = useState(null);
  const [rejectNote, setRejectNote] = useState({});
  // Pacing -- same overwhelm concern as Opportunities (Bradley, 2026-07-10):
  // only cap 'pending' (needs a real decision), not 'approved' (already
  // decided, just needs a send/post click -- much lower cognitive load).
  const [pendingPageSize, setPendingPageSize] = useState(10);

  // ---- Generate form state ----
  const [genPlatform, setGenPlatform] = useState("twitter");
  const [genType, setGenType] = useState("post");
  const [genLeadId, setGenLeadId] = useState("");
  const [genInstructions, setGenInstructions] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const isEmailDraft = genPlatform === "gmail";

  async function fetchRows() {
    setLoading(true);
    const { data, error } = await supabase
      .from("content_drafts")
      .select("*")
      .in("status", ["pending", "approved"])
      .order("created_at", { ascending: false });
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Failed to load content drafts." });
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    supabase
      .from("leads")
      .select("id, name, email")
      .order("name", { ascending: true })
      .limit(200)
      .then(({ data }) => setLeads(data || []));
    supabase.auth.getUser().then(({ data }) => setReviewerEmail(data?.user?.email ?? null));
  }, []);

  const setBusy = (id, on) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const leadById = useMemo(() => {
    const m = new Map();
    leads.forEach((l) => m.set(l.id, l));
    return m;
  }, [leads]);

  const leadsWithOpenDraft = useMemo(
    () => new Set(rows.filter((r) => r.lead_id).map((r) => r.lead_id)),
    [rows]
  );
  const availableLeads = useMemo(
    () => leads.filter((l) => !leadsWithOpenDraft.has(l.id)),
    [leads, leadsWithOpenDraft]
  );

  async function generateDraft(e) {
    e.preventDefault();
    if (genBusy) return;
    if (isEmailDraft && !genLeadId) {
      setBanner({ type: "error", msg: "Email drafts need a lead selected (that's who it sends to)." });
      return;
    }
    setGenBusy(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/draft-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({
          platform: genPlatform,
          type: isEmailDraft ? "email" : genType,
          lead_id: genLeadId || null,
          instructions: genInstructions || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Draft generation failed");
      setBanner({ type: "success", msg: "Draft generated." });
      setGenInstructions("");
      setGenLeadId("");
      setRows((prev) => [json.draft, ...prev]);
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", msg: err.message });
    } finally {
      setGenBusy(false);
    }
  }

  async function saveEdit(id, patch) {
    setBusy(id, true);
    const { error } = await supabase.from("content_drafts").update(patch).eq("id", id);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Save failed." });
    } else {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      setBanner({ type: "success", msg: "Draft updated." });
    }
    setBusy(id, false);
  }

  async function rewriteDraft(r) {
    const feedback = (rewriteFeedback[r.id] || "").trim();
    if (!feedback) return;
    setBusy(r.id, true);
    try {
      const isEmail = r.platform === "gmail";
      const res = await fetch(`${FUNCTIONS_URL}/draft-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({
          platform: r.platform,
          type: isEmail ? "email" : r.type,
          lead_id: r.lead_id || null,
          context_interaction_id: r.context_interaction_id || null,
          instructions: `Your previous draft was: "${r.draft_text}"${r.subject ? ` (subject: "${r.subject}")` : ""}\n\nBradley's feedback on it: ${feedback}\n\nRewrite it taking that feedback into account.`,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Rewrite failed");
      const fresh = json.draft;
      const patch = { draft_text: fresh.draft_text, ...(isEmail ? { subject: fresh.subject } : {}) };
      await supabase.from("content_drafts").update(patch).eq("id", r.id);
      await supabase.from("content_drafts").delete().eq("id", fresh.id);
      setRows((prev) => prev.map((row) => (row.id === r.id ? { ...row, ...patch } : row)));
      setEditText((prev) => ({ ...prev, [r.id]: fresh.draft_text }));
      if (isEmail) setEditSubject((prev) => ({ ...prev, [r.id]: fresh.subject }));
      setRewriteOpenId(null);
      setRewriteFeedback((prev) => ({ ...prev, [r.id]: "" }));
      setBanner({ type: "success", msg: "Rewritten." });
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", msg: err.message });
    } finally {
      setBusy(r.id, false);
    }
  }

  async function transition(id, status, notes) {
    setBusy(id, true);
    const patch = {
      status,
      reviewed_at: new Date().toISOString(),
      reviewed_by: reviewerEmail,
      ...(notes != null ? { reviewer_notes: notes || null } : {}),
    };
    const { error } = await supabase.from("content_drafts").update(patch).eq("id", id);
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Action failed." });
    } else {
      if (status === "approved") {
        setBanner({ type: "success", msg: "Draft approved. Scroll down to Send/Mark Posted." });
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      } else {
        // rejected and deleted both leave the queue -- nothing further to do.
        setBanner({
          type: "success",
          msg: status === "deleted" ? "Draft deleted." : "Draft rejected.",
        });
        setRows((prev) => prev.filter((r) => r.id !== id));
      }
    }
    setBusy(id, false);
  }

  async function submitReject(id) {
    const notes = (rejectNote[id] || "").trim();
    setRejectOpenId(null);
    await transition(id, "rejected", notes || null);
  }

  async function markPosted(id) {
    setBusy(id, true);
    const { error } = await supabase.rpc("mark_draft_posted", { p_draft_id: id });
    if (error) {
      console.error(error);
      setBanner({ type: "error", msg: "Mark posted failed: " + error.message });
    } else {
      setBanner({ type: "success", msg: "Marked posted and logged to interaction history." });
      setRows((prev) => prev.filter((r) => r.id !== id));
    }
    setBusy(id, false);
  }

  // Generic caller for all platform-specific post functions.
  async function callPoster(id, functionName, successMsg) {
    setBusy(id, true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/${functionName}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ draft_id: id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Post failed");
      setBanner({ type: "success", msg: successMsg });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      setBanner({ type: "error", msg: err.message });
    } finally {
      setBusy(id, false);
    }
  }

  const pendingAll = rows
    .filter((r) => r.status === "pending")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const pendingVisible = pendingAll.slice(0, pendingPageSize);
  const pendingWaiting = Math.max(0, pendingAll.length - pendingPageSize);
  const approvedAll = rows.filter((r) => r.status === "approved");
  const visibleRows = [...pendingVisible, ...approvedAll];

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ marginBottom: 12 }}>Content Drafts</h1>

      <Banner notice={banner} />

      {/* Generate form */}
      <form
        onSubmit={generateDraft}
        style={{ border: "1px solid #eee", padding: 12, borderRadius: 10, marginBottom: 16 }}
      >
        <div style={{ display: "grid", gridTemplateColumns: isEmailDraft ? "140px 1fr 2fr auto" : "140px 120px 1fr 2fr auto", gap: 8 }}>
          <select value={genPlatform} onChange={(e) => setGenPlatform(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}>
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {!isEmailDraft && (
            <select value={genType} onChange={(e) => setGenType(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}>
              {SOCIAL_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
          <select value={genLeadId} onChange={(e) => setGenLeadId(e.target.value)} style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}>
            <option value="">{isEmailDraft ? "Select a lead (required)" : "(no lead)"}</option>
            {availableLeads.map((l) => (
              <option key={l.id} value={l.id}>{l.name}{isEmailDraft ? ` <${l.email || "no email"}>` : ""}</option>
            ))}
          </select>
          <input
            placeholder="Optional direction, e.g. 'observational post about a gallery visit'"
            value={genInstructions}
            onChange={(e) => setGenInstructions(e.target.value)}
            style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
          />
          <button
            type="submit"
            disabled={genBusy}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #c8e6c9", background: genBusy ? "#d7e8da" : "#e8f5e9", cursor: genBusy ? "not-allowed" : "pointer" }}
          >
            {genBusy ? "Generating…" : "Generate Draft"}
          </button>
        </div>
      </form>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <button onClick={fetchRows} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}>
          Refresh
        </button>
        <span style={{ fontSize: 12, color: "#888" }}>Sent items move to the lead's own page. Rejected/deleted drafts drop from view.</span>
        <select
          value={pendingPageSize}
          onChange={(e) => setPendingPageSize(Number(e.target.value))}
          style={{ marginLeft: "auto", padding: 8, borderRadius: 8, border: "1px solid #ddd", fontSize: 13 }}
        >
          <option value={5}>Show 5 pending at a time</option>
          <option value={10}>Show 10 pending at a time</option>
          <option value={20}>Show 20 pending at a time</option>
          <option value={9999}>Show all pending</option>
        </select>
      </div>

      {pendingWaiting > 0 && (
        <div style={{ padding: "10px 12px", marginBottom: 12, borderRadius: 8, fontSize: 14, background: "#e3f2fd", border: "1px solid #bbdefb", color: "#0d47a1" }}>
          Showing the oldest {pendingVisible.length} of {pendingAll.length} pending — {pendingWaiting} more waiting until you clear some of these.
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <div>No drafts.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visibleRows.map((r) => {
            const busy = busyIds.has(r.id);
            const isEmail = r.platform === "gmail";
            const isInstagram = r.platform === "instagram";
            const isTelegram = r.platform === "telegram";
            const text = editText[r.id] ?? r.draft_text;
            const subject = editSubject[r.id] ?? r.subject ?? "";
            const imageUrl = editImageUrl[r.id] ?? r.image_url ?? "";
            const textDirty = editText[r.id] != null && editText[r.id] !== r.draft_text;
            const subjectDirty = editSubject[r.id] != null && editSubject[r.id] !== r.subject;
            const imageUrlDirty = editImageUrl[r.id] != null && editImageUrl[r.id] !== (r.image_url ?? "");
            const anyDirty = textDirty || subjectDirty || imageUrlDirty;

            return (
              <div key={r.id} style={{ border: "1px solid #eee", borderRadius: 10, padding: 12 }}>
                <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#666", marginBottom: 4 }}>
                  <span style={{ textTransform: "uppercase" }}>{isEmail ? "EMAIL" : r.platform}</span>
                  {!isEmail && <><span>·</span><span>{r.type}</span></>}
                  <span>·</span>
                  <span>{r.status}</span>
                  <span style={{ marginLeft: "auto" }}>{new Date(r.created_at).toLocaleString()}</span>
                </div>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                  {r.lead_id ? (
                    leadById.get(r.lead_id)
                      ? `To: ${leadById.get(r.lead_id).name} <${leadById.get(r.lead_id).email || "no email on file"}>`
                      : `To: (lead ${r.lead_id} — no longer in Leads)`
                  ) : (
                    "No lead attached"
                  )}
                </div>

                {isEmail && (
                  <input
                    value={subject}
                    onChange={(e) => setEditSubject((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    disabled={r.status !== "pending" || busy}
                    placeholder="Subject"
                    style={{ width: "100%", padding: 8, marginBottom: 8, borderRadius: 8, border: "1px solid #ddd", fontWeight: 600 }}
                  />
                )}

                <textarea
                  value={text}
                  onChange={(e) => setEditText((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  rows={isEmail ? 6 : 3}
                  disabled={r.status !== "pending" || busy}
                  style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #ddd", fontFamily: "inherit" }}
                />

                {/* Image URL: required for Instagram, optional for Telegram */}
                {(isInstagram || isTelegram) && (
                  <div style={{ marginTop: 6 }}>
                    <input
                      value={imageUrl}
                      onChange={(e) => setEditImageUrl((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      disabled={r.status !== "pending" || busy}
                      placeholder={isInstagram ? "Image URL (required — must be a public HTTPS URL)" : "Image URL (optional)"}
                      style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${isInstagram && !imageUrl ? "#ffcdd2" : "#ddd"}`, fontSize: 13 }}
                    />
                    {imageUrl && (
                      <div style={{ marginTop: 4 }}>
                        <img
                          src={imageUrl}
                          alt="preview"
                          style={{ maxHeight: 120, maxWidth: "100%", objectFit: "cover", borderRadius: 6, border: "1px solid #eee" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {r.status === "pending" && anyDirty ? (
                    <button
                      onClick={() => saveEdit(r.id, {
                        draft_text: text,
                        ...(isEmail ? { subject } : {}),
                        ...((isInstagram || isTelegram) ? { image_url: imageUrl || null } : {}),
                      })}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}
                    >
                      Save Edit
                    </button>
                  ) : null}

                  {r.status === "pending" ? (
                    <>
                      <button
                        onClick={() => transition(r.id, "approved")}
                        disabled={busy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #c8e6c9", background: busy ? "#d7e8da" : "#e8f5e9", cursor: busy ? "not-allowed" : "pointer" }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setRewriteOpenId(rewriteOpenId === r.id ? null : r.id)}
                        disabled={busy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #d1c4e9", background: busy ? "#e6ddf0" : "#ede7f6", cursor: busy ? "not-allowed" : "pointer" }}
                      >
                        Rewrite with feedback
                      </button>
                      <button
                        onClick={() => {
                          setRejectOpenId(rejectOpenId === r.id ? null : r.id);
                          setRewriteOpenId(null);
                        }}
                        disabled={busy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ffcdd2", background: busy ? "#f3d6da" : "#ffebee", cursor: busy ? "not-allowed" : "pointer" }}
                      >
                        No (+ explain why)
                      </button>
                      <button
                        onClick={() => transition(r.id, "deleted")}
                        disabled={busy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e0e0e0", background: busy ? "#eeeeee" : "#f5f5f5", cursor: busy ? "not-allowed" : "pointer", color: "#757575" }}
                      >
                        Delete
                      </button>
                    </>
                  ) : null}

                  {/* Approved-state actions */}
                  {r.status === "approved" && isEmail ? (
                    <button
                      onClick={() => callPoster(r.id, "send-approved-email", "Email sent and logged to interaction history.")}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #bbdefb", background: busy ? "#cfe3f7" : "#e3f2fd", cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      {busy ? "Sending…" : "Send Email"}
                    </button>
                  ) : null}
                  {r.status === "approved" && r.platform === "twitter" ? (
                    <button
                      onClick={() => callPoster(r.id, "post-to-twitter", "Posted to Twitter.")}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #bbdefb", background: busy ? "#cfe3f7" : "#e3f2fd", cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      {busy ? "Posting…" : "Post to Twitter"}
                    </button>
                  ) : null}
                  {r.status === "approved" && r.platform === "discord" ? (
                    <button
                      onClick={() => callPoster(r.id, "post-to-discord", "Posted to Discord.")}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #bbdefb", background: busy ? "#cfe3f7" : "#e3f2fd", cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      {busy ? "Posting…" : "Post to Discord"}
                    </button>
                  ) : null}
                  {r.status === "approved" && r.platform === "instagram" ? (
                    <button
                      onClick={() => callPoster(r.id, "post-to-instagram", "Posted to Instagram.")}
                      disabled={busy || !r.image_url}
                      title={!r.image_url ? "Set an image URL (save edit first) before posting to Instagram" : undefined}
                      style={{
                        padding: "6px 10px", borderRadius: 8,
                        border: "1px solid #f8bbd9", background: busy ? "#f5d0e7" : "#fce4ec",
                        cursor: (busy || !r.image_url) ? "not-allowed" : "pointer",
                        opacity: !r.image_url ? 0.6 : 1,
                      }}
                    >
                      {busy ? "Posting…" : r.image_url ? "Post to Instagram" : "Post to Instagram (set image URL first)"}
                    </button>
                  ) : null}
                  {r.status === "approved" && r.platform === "telegram" ? (
                    <button
                      onClick={() => callPoster(r.id, "post-to-telegram", "Posted to Telegram.")}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #b3e5fc", background: busy ? "#c5eef9" : "#e1f5fe", cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      {busy ? "Posting…" : "Post to Telegram"}
                    </button>
                  ) : null}
                  {r.status === "approved" && !isEmail && !["twitter", "discord", "instagram", "telegram"].includes(r.platform) ? (
                    <button
                      onClick={() => markPosted(r.id)}
                      disabled={busy}
                      style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #bbdefb", background: busy ? "#cfe3f7" : "#e3f2fd", cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      Mark Posted (I posted this manually)
                    </button>
                  ) : null}
                </div>

                {/* Rewrite panel */}
                {rewriteOpenId === r.id && (
                  <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: "1px solid #d1c4e9", background: "#f5f2fa" }}>
                    <textarea
                      value={rewriteFeedback[r.id] || ""}
                      onChange={(e) => setRewriteFeedback((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      placeholder="What's wrong with it? e.g. 'too formal' or 'doesn't sound like her'"
                      rows={2}
                      autoFocus
                      style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #ddd", fontFamily: "inherit" }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button
                        onClick={() => rewriteDraft(r)}
                        disabled={busy || !(rewriteFeedback[r.id] || "").trim()}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #d1c4e9", background: "#ede7f6", cursor: "pointer" }}
                      >
                        {busy ? "Rewriting…" : "Rewrite"}
                      </button>
                      <button onClick={() => setRewriteOpenId(null)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Reject panel: optional note for Claire to learn from */}
                {rejectOpenId === r.id && (
                  <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: "1px solid #ffcdd2", background: "#fff8f8" }}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>
                      Reason for Claire to learn from (optional — submit empty to just reject):
                    </div>
                    <textarea
                      value={rejectNote[r.id] || ""}
                      onChange={(e) => setRejectNote((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      placeholder="e.g. 'Too promotional for this platform' or 'Wrong tone for this lead'"
                      rows={2}
                      autoFocus
                      style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #ddd", fontFamily: "inherit" }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button
                        onClick={() => submitReject(r.id)}
                        disabled={busy}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ffcdd2", background: "#ffebee", cursor: "pointer" }}
                      >
                        {busy ? "Rejecting…" : "Reject"}
                      </button>
                      <button onClick={() => setRejectOpenId(null)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fafafa" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
